import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { Button, Dialog, Input, SpinLoading } from "antd-mobile";
import useNavigation from "../../hooks/useNavigation";
import PageWrap from "../components/PageWrap";
import PageHeader, { headerHeight } from "../components/PageHeader";
import { useStores } from "../../models";
import { ToastError } from "../../components/Toast";
import { formatPrice, staticUrl } from "../../utils";
import {
  CreditStatus,
  PayTypeModal,
  ProtocolConfirmModal,
  payTypeImage,
} from "../../components/Wallet";
import Avatar from "../../components/Avatar";
import useGlobalTheme from "../../hooks/useGlobalTheme";

const ScrollArea = styled.div`
  height: calc(100% - ${headerHeight} - 72px);
  overflow: auto;
  background: #fff;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid #ccc;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: #222;
`;

const Muted = styled.div`
  font-size: 13px;
  color: #999;
`;

const Footer = styled.div`
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  background: #fff;
  border-top: 1px solid #eee;
`;

const Tip = styled.div`
  font-size: 12px;
  color: #aaa;
  line-height: 1.6;
  margin-bottom: 6px;
`;

const PayDetail = () => {
  const theme = useGlobalTheme();
  const { navigate, params } = useNavigation();
  const { orderStore, walletStore } = useStores();
  const [loading, setLoading] = useState(false);
  const [booting, setBooting] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<any>(null);
  const [quantity, setQuantity] = useState("");
  const [payImage, setPayImage] = useState<string | null>(null);
  const [selectedProtocol, setSelectedProtocol] = useState(false);
  const [protocolVisible, setProtocolVisible] = useState(false);

  const orderNo = params.order_no || params.order_id;
  const order = orderStore.orders.get(orderNo) as any;
  const { is_id_verify, is_phone_verify } = walletStore;

  useEffect(() => {
    const boot = async () => {
      try {
        setBooting(true);
        await Promise.all([
          walletStore.loadPaymentList(),
          orderStore.loadOrderlist(),
          walletStore.getWalletInfo(),
        ]);
      } finally {
        setBooting(false);
      }
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNo]);

  if (booting) {
    return (
      <PageWrap>
        <PageHeader title="下单" />
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <SpinLoading />
        </div>
      </PageWrap>
    );
  }

  if (!order) {
    return (
      <PageWrap>
        <PageHeader title="下单" />
        <div style={{ padding: 40, textAlign: "center" }}>订单不存在或已下架</div>
      </PageWrap>
    );
  }

  const {
    name,
    quantities,
    amount,
    stock,
    payment_methods,
    user_id,
    order_no,
    credit_score,
    settled_count,
    min_trading,
    avatar,
  } = order;

  const handlesetQuantity = (text: string) => {
    if (text === "" || !Number(text)) {
      setQuantity("");
      return;
    }
    if (/^[0-9]+(\.[0-9]{0,2})?$/.test(text)) {
      if (Number(text) > Number(stock)) setQuantity(String(stock));
      else setQuantity(text);
    }
  };

  const checkPayment = () => {
    let matched = false;
    (walletStore.paymentList || []).forEach((p: any) => {
      if (payment_methods?.includes(p.pay_type)) matched = true;
    });
    if (matched) {
      setIsModalVisible(true);
    } else {
      Dialog.confirm({
        title: "提示",
        content: "未有对应的支付方式, 请先完成绑定",
        confirmText: "前往绑定",
        cancelText: "之后再说",
        onConfirm: () => navigate("PayAccountManage"),
      });
    }
  };

  const takeOrder = () => {
    if (!is_id_verify) {
      Dialog.confirm({
        title: "提示",
        content: "请先绑定实名认证,再进行下单",
        confirmText: "前往绑定",
        cancelText: "之后再说",
        onConfirm: () => navigate("Verified"),
      });
      return;
    }
    if (!is_phone_verify) {
      Dialog.confirm({
        title: "提示",
        content: "请先绑定手机,再进行下单",
        confirmText: "前往绑定",
        cancelText: "之后再说",
        onConfirm: () => {
          Dialog.clear();
          navigate("PhoneBinding");
        },
      });
      return;
    }
    if (!Number(quantity)) return ToastError("请输入正确购买数量");
    if (/\./g.test(`${quantity}`)) return ToastError("转账数量仅支持输入整数");
    if (Number(quantity) < Number(min_trading))
      return ToastError(`购买数量不能小于${min_trading}`);
    if (Number(quantity) > Number(stock))
      return ToastError(`购买数量不可大于库存量${stock}`);
    if (!paymentInfo) {
      setIsModalVisible(true);
      return;
    }

    const payload = {
      seller_id: user_id,
      order_no,
      quantities: Number(quantity),
      payment_method: paymentInfo.pay_type,
    };
    setLoading(true);
    orderStore.publicTakeOrderNew(
      payload,
      () => {
        walletStore.getWalletInfo();
        setLoading(false);
      },
      (error: any) => {
        ToastError(error?.message || "下单失败");
        setLoading(false);
      }
    );
  };

  const getTotal = () => formatPrice(Number(quantity || 0) * Number(amount || 0));
  const total = Number(quantities) || 0;
  const _stock = Number(stock) || 0;
  const progress =
    "成单量 " +
    (total - _stock).toString() +
    " | " +
    (total ? Math.round((_stock / total) * 10000) / 100 : 0) +
    "%";

  const pickImage = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      setPayImage(URL.createObjectURL(file));
    };
    input.click();
  };

  return (
    <PageWrap>
      <PageHeader title="下单" />
      <ScrollArea>
        <Row>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Avatar avatar={staticUrl(avatar)} name={name} />
            <Title style={{ marginLeft: 8 }}>{name}</Title>
          </div>
          <CreditStatus credit_score={credit_score} />
        </Row>

        <Row>
          <div style={{ flex: 1, textAlign: "center" }}>
            <Title style={{ fontSize: 22 }}>{stock}</Title>
            <Muted>喜鹊币</Muted>
          </div>
          <div style={{ flex: 1, textAlign: "center", position: "relative" }}>
            <Title style={{ fontSize: 22 }}>{amount}</Title>
            <span
              style={{
                position: "absolute",
                top: -8,
                right: 40,
                background: theme.colors.primary,
                color: "#fff",
                borderRadius: 12,
                padding: "0 6px",
                fontSize: 11,
              }}
            >
              单价
            </span>
            <Muted>CNY</Muted>
          </div>
        </Row>

        <Row>
          <Muted>{progress}</Muted>
          <Muted>与我成交：{settled_count ?? 0}</Muted>
        </Row>

        <Row>
          <Title>卖家收款方式</Title>
          <div style={{ display: "flex" }}>
            {(payment_methods || []).map((type: number) => (
              <img
                key={type}
                src={payTypeImage(type)}
                alt=""
                width={20}
                height={20}
                style={{ marginLeft: 5, borderRadius: 5 }}
              />
            ))}
          </div>
        </Row>

        <Row style={{ cursor: "pointer" }} onClick={checkPayment}>
          <Title>付款方式</Title>
          {!paymentInfo ? (
            <Muted>请选择付款方式</Muted>
          ) : (
            <img
              src={payTypeImage(paymentInfo.pay_type)}
              alt=""
              width={20}
              height={20}
            />
          )}
        </Row>

        <Row>
          <Title>购买数量</Title>
          <div style={{ display: "flex", alignItems: "center", flex: 1.4, justifyContent: "flex-end" }}>
            <Input
              value={quantity}
              onChange={handlesetQuantity}
              placeholder={`购买数量需介于${min_trading}~${stock}之间`}
              style={{ "--text-align": "right", fontSize: 12, maxWidth: 180 } as any}
            />
            <Button
              size="mini"
              fill="none"
              color="primary"
              onClick={() => setQuantity(String(stock))}
            >
              最大
            </Button>
          </div>
        </Row>

        <Row>
          <Title>实付款</Title>
          <Muted>≈{getTotal()}CNY</Muted>
        </Row>

        <Row style={{ cursor: "pointer" }} onClick={pickImage}>
          <Title>历史流水</Title>
          {payImage ? (
            <img src={payImage} alt="" width={48} height={48} style={{ objectFit: "cover" }} />
          ) : (
            <Muted>点击上传</Muted>
          )}
        </Row>

        <div style={{ padding: "12px 20px" }}>
          <Title style={{ marginBottom: 8 }}>温馨提示</Title>
          <Tip>1.实名认证：请使用平台实名信息一致的账户付款</Tip>
          <Tip>
            2.信用等级：成交单量，成交效率将影响您的信用等级，等级越高的用户推送越靠前
          </Tip>
          <Tip>3.交易：请实时留意订单状态</Tip>
          <Tip>4.出售：请务必确认收到款项后再进行打币</Tip>
          <Tip>
            5.订单完成：确认收款后，请尽快打币完成交易，订单出现异常请第一时间暂停交易并申诉；
          </Tip>
        </div>
      </ScrollArea>

      <Footer>
        <Button
          color="primary"
          block
          loading={loading}
          onClick={() => {
            if (!paymentInfo) checkPayment();
            else if (selectedProtocol) takeOrder();
            else setProtocolVisible(true);
          }}
        >
          立即下单
        </Button>
      </Footer>

      <PayTypeModal
        paymentMethods={payment_methods}
        callBack={setPaymentInfo}
        title="选择付款方式"
        visible={isModalVisible}
        setVisible={setIsModalVisible}
      />
      <ProtocolConfirmModal
        visible={protocolVisible}
        selected={selectedProtocol}
        onSelected={setSelectedProtocol}
        onConfirm={takeOrder}
        onClose={() => setProtocolVisible(false)}
      />
    </PageWrap>
  );
};

export default observer(PayDetail);
